import { PaymentMethodsResponse } from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/types';

import { AdyenCaptureCreditCardsProps } from '@features/AdyenCreditCard/AdyenCaptureCreditCard/AdyenCaptureCreditCard';
import { AdyenManageCreditCardsProps } from '@features/AdyenCreditCard/AdyenManageCreditCards/AdyenManageCreditCards';
import { AdyenApplePayButtonWrapperProps } from '@features/ApplePay/AdyenApplePayButtonWrapper/AdyenApplePayButtonWrapper';
import { BancontactAdyenManageCreditCardsProps } from '@features/BancontactAdyenCreditCard/BancontactAdyenManageCreditCards/BancontactAdyenManageCreditCards';
import { GooglePaySubmitFn } from '@features/GooglePay/GooglePayButton/GooglePayButton.static';
import { Environment, MaybeNullable, Nullable } from '@types-internal';
import {
  ParseAdyenApplePayButtonWrapperProps,
  ParsedAdyenCaptureCreditCardsProps,
  ParsedAdyenManageCreditCardsProps,
  ParsedBancontactAdyenManageCreditCardsProps,
  ParsedCoreProvidersProps,
  ParseGooglePayButtonWrapperProps,
} from '@types-internal/ExtractHtmlTypes';

import {
  checkMyAccountSetting,
  getNullKeys,
  nonEmptyString,
  parseAdyenCreditCardData,
  parseCreditCardBrands,
  parseCustomerType,
  parseLocale,
  parsePossibleCreditCardBrands,
  parseMaybeCustomerType,
  parseShopBundle,
  parseAmount,
  parseBancontactAdyenCreditCardData,
} from './extractIndividualDataFromHtml';
import { convertRunningEnvToAdyenEnv, safeStringify } from './utils';

function parseCoreProvidersProps(
  container: HTMLElement,
  runningEnv: Environment,
): ParsedCoreProvidersProps {
  return {
    shopBundle: parseShopBundle(container),
    domain: nonEmptyString(container, 'domain'),
    locale: parseLocale(container),
    errorContainerId: nonEmptyString(container, 'errorContainerId'),
    csrfToken: container.dataset.csrfToken,
    env: runningEnv,
  };
}

/*
 * Parsing to get the props of the AdyenManageCreditCards.
 */
export function parseAdyenCaptureCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
  maybeCustomerType: MaybeNullable<string>,
  maybeAdyenCardBrandCodes: unknown,
): ParsedAdyenCaptureCreditCardsProps {
  return {
    ...parseCoreProvidersProps(container, runningEnv),
    currency: nonEmptyString(container, 'currency'),
    customerType: parseMaybeCustomerType(maybeCustomerType),
    isFallbackPsp: container.dataset.fallbackPsp === 'true',
    adyenEnvironment: convertRunningEnvToAdyenEnv(runningEnv),
    supportedCreditCardBrands: parsePossibleCreditCardBrands(maybeAdyenCardBrandCodes),
  };
}

export function normalizeAdyenCaptureCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
  maybeCustomerType: MaybeNullable<string>,
  maybeAdyenCardBrandCodes: unknown,
): AdyenCaptureCreditCardsProps {
  const props = parseAdyenCaptureCreditCardsProps(
    container,
    runningEnv,
    maybeCustomerType,
    maybeAdyenCardBrandCodes,
  );
  const {
    errorContainerId,
    shopBundle,
    domain,
    locale,
    customerType,
    currency,
    adyenEnvironment,
    supportedCreditCardBrands,
  } = props;

  if (
    errorContainerId === null ||
    shopBundle === null ||
    domain === null ||
    locale === null ||
    customerType === null ||
    currency === null ||
    adyenEnvironment === null ||
    supportedCreditCardBrands === null
  ) {
    throw new Error(
      `[AdyenCaptureCreditCards] Got null keys:${safeStringify(
        getNullKeys(props),
      )} in the container dataset: ${safeStringify(container.dataset)}`,
    );
  } else {
    return {
      errorContainerId,
      shopBundle,
      domain,
      locale,
      customerType,
      currency,
      adyenEnvironment,
      supportedCreditCardBrands,
      csrfToken: props.csrfToken,
      isFallbackPsp: props.isFallbackPsp,
      env: props.env,
    };
  }
}

/*
 * Parsing to get the props of the AdyenManageCreditCards.
 */
export function parseAdyenManageCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
): ParsedAdyenManageCreditCardsProps {
  return {
    ...parseCoreProvidersProps(container, runningEnv),
    currency: nonEmptyString(container, 'currency'),
    containerType: checkMyAccountSetting(container),
    customerType: parseCustomerType(container),
    isFallbackPsp: container.dataset.fallbackPsp === 'true',
    adyenEnvironment: convertRunningEnvToAdyenEnv(runningEnv),
    supportedCreditCardBrands: parseCreditCardBrands(container),
    creditCards: parseAdyenCreditCardData(container),
    isRouteToAdyenDueToMarketplace: container.dataset.routedToAdyenDueToMarketplace === 'true',
    customerHasCreditCardAutoshipment:
      container.dataset.customerHasCreditCardAutoshipment === 'true',
    isCartWithMarketplaceItems: container.dataset.cartWithMarketplaceItems === 'true',
  };
}

export function normalizeAdyenManageCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
): AdyenManageCreditCardsProps {
  const props = parseAdyenManageCreditCardsProps(container, runningEnv);
  const {
    errorContainerId,
    shopBundle,
    domain,
    locale,
    customerType,
    currency,
    adyenEnvironment,
    supportedCreditCardBrands,
    creditCards,
    isRouteToAdyenDueToMarketplace,
    customerHasCreditCardAutoshipment,
    isCartWithMarketplaceItems,
  } = props;

  if (
    errorContainerId === null ||
    shopBundle === null ||
    domain === null ||
    locale === null ||
    customerType === null ||
    currency === null ||
    adyenEnvironment === null ||
    supportedCreditCardBrands === null ||
    creditCards === null
  ) {
    throw new Error(
      `[AdyenManageCreditCards] Got null keys:${safeStringify(
        getNullKeys(props),
      )} in the container dataset: ${safeStringify(container.dataset)}`,
    );
  } else {
    return {
      errorContainerId,
      shopBundle,
      domain,
      locale,
      containerType: props.containerType,
      customerType,
      currency,
      adyenEnvironment,
      supportedCreditCardBrands,
      creditCards,
      csrfToken: props.csrfToken,
      isFallbackPsp: props.isFallbackPsp,
      env: props.env,
      isRouteToAdyenDueToMarketplace,
      customerHasCreditCardAutoshipment,
      isCartWithMarketplaceItems,
    };
  }
}

/**
 * Function to parse the props of the BancontactAdyenManageCreditCards.
 *
 * @param container HTMLElement container where the props are stored
 * @param runningEnv Environment where the application is running
 * @returns ParsedBancontactAdyenManageCreditCardsProps to be used in the component
 */
export function parseBancontactAdyenManageCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
): ParsedBancontactAdyenManageCreditCardsProps {
  return {
    ...parseCoreProvidersProps(container, runningEnv),
    currency: nonEmptyString(container, 'currency'),
    containerType: checkMyAccountSetting(container),
    customerType: parseCustomerType(container),
    isFallbackPsp: container.dataset.fallbackPsp === 'true',
    adyenEnvironment: convertRunningEnvToAdyenEnv(runningEnv),
    creditCards: parseBancontactAdyenCreditCardData(container),
  };
}

export function normalizeBancontactAdyenManageCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
): BancontactAdyenManageCreditCardsProps {
  const props = parseBancontactAdyenManageCreditCardsProps(container, runningEnv);
  const {
    errorContainerId,
    shopBundle,
    domain,
    locale,
    customerType,
    currency,
    adyenEnvironment,
    creditCards,
  } = props;

  if (
    errorContainerId === null ||
    shopBundle === null ||
    domain === null ||
    locale === null ||
    customerType === null ||
    currency === null ||
    adyenEnvironment === null ||
    creditCards === null
  ) {
    throw new Error(
      `[BancontactAdyenManageCreditCards] Got null keys:${safeStringify(
        getNullKeys(props),
      )} in the container dataset: ${safeStringify(container.dataset)}`,
    );
  } else {
    return {
      errorContainerId,
      shopBundle,
      domain,
      locale,
      containerType: props.containerType,
      customerType,
      currency,
      adyenEnvironment,
      creditCards,
      csrfToken: props.csrfToken,
      isFallbackPsp: props.isFallbackPsp,
      env: props.env,
    };
  }
}

/**
 * AdyenApplePayButton_Tmp
 */
export function parseAdyenApplePayButtonWrapperProps(
  container: HTMLElement,
  runningEnv: Environment,
  maybePaymentMethodsResponse: unknown,
  maybeOnSubmit: unknown,
  maybeOnError: unknown,
): ParseAdyenApplePayButtonWrapperProps {
  return {
    ...parseCoreProvidersProps(container, runningEnv),
    adyenEnvironment: convertRunningEnvToAdyenEnv(runningEnv),
    countryCode: nonEmptyString(container, 'countryCode'),
    currency: nonEmptyString(container, 'currency'),
    amount: parseAmount(container),
    paymentMethodsResponse: maybePaymentMethodsResponse,
    onSubmit:
      typeof maybeOnSubmit === 'function'
        ? (maybeOnSubmit as (applePayToken: string) => void)
        : null,
    onError:
      typeof maybeOnError === 'function'
        ? (maybeOnError as (errorType: Nullable<string>) => void)
        : null,
  };
}

/**
 * GooglePayButton
 */
export function parseGooglePayButtonWrapperProps(
  container: HTMLElement,
  runningEnv: Environment,
  maybePaymentMethodsResponse: unknown,
  maybeOnSubmit: unknown,
  maybeOnError: unknown,
): ParseGooglePayButtonWrapperProps {
  let onSubmit = null;
  let onError = null;
  if (typeof maybeOnSubmit === 'function') {
    onSubmit = maybeOnSubmit as GooglePaySubmitFn;
  }
  if (typeof maybeOnError === 'function') {
    onError = maybeOnError as (errorType: Nullable<string>) => void;
  }

  return {
    ...parseCoreProvidersProps(container, runningEnv),
    onError,
    onSubmit,
    adyenEnvironment: convertRunningEnvToAdyenEnv(runningEnv),
    countryCode: nonEmptyString(container, 'countryCode'),
    currency: nonEmptyString(container, 'currency'),
    amount: parseAmount(container),
    paymentMethodsResponse: maybePaymentMethodsResponse,
  };
}

export function normalizeAdyenApplePayButtonWrapperProps(
  container: HTMLElement,
  runningEnv: Environment,
  maybePaymentMethodsResponse: unknown,
  maybeOnSubmit: unknown,
  maybeOnError: unknown,
): AdyenApplePayButtonWrapperProps {
  const props = parseAdyenApplePayButtonWrapperProps(
    container,
    runningEnv,
    maybePaymentMethodsResponse,
    maybeOnSubmit,
    maybeOnError,
  );
  const {
    errorContainerId,
    shopBundle,
    domain,
    locale,
    adyenEnvironment,
    countryCode,
    currency,
    amount,
    paymentMethodsResponse,
    onSubmit,
    onError,
  } = props;

  if (
    errorContainerId === null ||
    shopBundle === null ||
    domain === null ||
    locale === null ||
    adyenEnvironment === null ||
    countryCode === null ||
    currency === null ||
    amount === null ||
    paymentMethodsResponse === null ||
    onSubmit === null ||
    onError === null
  ) {
    throw new Error(
      `[AdyenApplePayButton] Got null keys:${safeStringify(
        getNullKeys(props),
      )} in the container dataset: ${safeStringify(container.dataset)}`,
    );
  } else {
    return {
      errorContainerId,
      shopBundle,
      domain,
      locale,
      adyenEnvironment,
      amount,
      countryCode,
      currency,
      paymentMethodsResponse: paymentMethodsResponse as PaymentMethodsResponse,
      onSubmit,
      onError,
      env: props.env,
    };
  }
}
